import { useContext } from 'react';
import { WorkflowContext, type WorkflowContextValue } from '../contexts/WorkflowContextDef';

/**
 * Custom hook to access the workflow context.
 *
 * MUST be used within a WorkflowProvider. Throws an error with a helpful
 * message if used outside the provider hierarchy.
 *
 * @returns The workflow context value containing all state and operations
 * @throws Error if used outside of WorkflowProvider
 *
 * @example
 * ```tsx
 * function BatchList() {
 *   const { batches, loading, toggleSelection, isSelected } = useWorkflow();
 *
 *   if (loading) return <LoadingSpinner />;
 *
 *   return (
 *     <ul>
 *       {batches.map(batch => (
 *         <li key={batch.id}>
 *           <Checkbox
 *             checked={isSelected(batch.id)}
 *             onChange={() => toggleSelection(batch.id)}
 *           />
 *           {batch.batchNumber}
 *         </li>
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export function useWorkflow(): WorkflowContextValue {
  const context = useContext(WorkflowContext);

  if (!context) {
    throw new Error(
      'useWorkflow must be used within a WorkflowProvider. ' +
        'Wrap your component tree with <WorkflowProvider> to fix this error.'
    );
  }

  return context;
}

// Re-export the type for convenience
export type { WorkflowContextValue } from '../contexts/WorkflowContextDef';
